import {
  ContentBlocks_AssetSlider,
  ContentBlocks_AssetSlider_Slide,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/content_blocks.types';
import {
  ContentBlockMedia,
  ContentBlockText,
  HorizontalSlider,
  RowSection,
  SliderControlProps,
  SliderWrapper,
  mediaLargeScreens,
  mediaTabletAndUp,
  mobileOnly,
  tabletAndUp,
} from '@aboutyou/ui-components';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC, useCallback, useId} from 'react';
import sc, {css} from 'styled-components';
import {CTA} from '../CTA/CTA';
import {useContentBlockPromotionTracking} from '../helpers/useContentBlockPromotionTracking';
import {useContentBlockPromotionTrackingImpression} from '../helpers/useContentBlockPromotionTrackingImpression';
import {useTrackVideoPromotion} from '../helpers/useTrackVideoPromotion';
import {alignmentMap, fontMap, transformImageVideo} from '../transforms';
import {SliderDottedNavigation} from './SliderDottedNavigation';

interface AssetSliderProps {
  content: ContentBlocks_AssetSlider;
  constrained?: boolean;
  hideSideButtons?: boolean;
  blockId?: number;
  testId?: string;
}

export const AssetSliderModule: React.FC<AssetSliderProps> = ({
  content,
  constrained,
  hideSideButtons,
  blockId,
  testId = 'customPageAssetSliderModule',
}) => {
  const SlideControlComponent = useCallback(
    (props: SliderControlProps) => (
      <SliderDottedNavigation count={content.slides.length} {...props} />
    ),
    [content.slides.length],
  );

  const uniqueId = useId();

  const placementImpressionpromotionName = content.headline
    ? content.headline.text
    : '';

  const {elementRef: placementRef} =
    useContentBlockPromotionTrackingImpression<HTMLDivElement>({
      blockId,
      promotionName: placementImpressionpromotionName,
      promotionId: blockId ? String(blockId) : '',
      locationBlockType: 'assetSlider',
      path: {external: false, url: ''},
      contentBlockSource: 'banner',
    });

  if (!content) {
    return null;
  }

  return (
    <StyledRowSection
      $backgroundColor={content.backgroundColor}
      constrained={true}
      data-testid={`${testId}`}
      data-content-block={blockId}
      ref={placementRef}
    >
      {content.headline && (
        <StyledSliderTitle
          testId={`${testId}_Headline`}
          alignment={alignmentMap[content.headline.alignment]}
          color={content.headline.color}
          size={fontMap[content.headline.size]}
          text={content.headline.text}
          weight={'bold'}
        />
      )}

      <HorizontalSliderWrapper>
        <StyledHorizontalSlider
          dataTestId={`${testId}_Slider`}
          disableHeaderControl
          constrained={constrained}
          hideSideButtons={hideSideButtons}
          slideCount={1}
          enableKeyboardNavigation
          SlideControlComponentBottom={SlideControlComponent}
        >
          {content.slides.map((slide, index) => (
            <Slide
              key={`assetSlider-${uniqueId}-slide-${index}`}
              slide={slide}
              testId={testId}
              slideIndex={index}
              blockId={blockId}
            />
          ))}
        </StyledHorizontalSlider>
      </HorizontalSliderWrapper>
    </StyledRowSection>
  );
};

const Slide: FC<{
  slide: ContentBlocks_AssetSlider_Slide;
  slideIndex: number;
  blockId?: number;
  testId: string;
}> = ({slide, slideIndex, blockId, testId}) => {
  const promotionTrackingClick = useContentBlockPromotionTracking();

  const promotionId: string = slide.id;
  const promotionName = (() => {
    if (slide.cta) {
      return slide.cta.text.text;
    } else if (slide.headline) {
      return slide.headline.text;
    }

    return '';
  })();
  const locationBlockType = 'assetSlider';

  const {elementRef: videoElementRef, trackFeatureOnVideoEnds} =
    useTrackVideoPromotion({
      blockId,
      promotionId,
      promotionName,
      locationBlockType,
      path: {
        external: true,
        url: 'video',
      },
    });

  const handleCtaClick = (
    promotionName: string,
    path: {url: string; external: boolean},
    streamPosition: number,
  ) => {
    promotionTrackingClick({
      actionType: 'click',
      contentBlockSource: 'cta',
      locationBlockType,
      promotionId: blockId ? String(blockId) : '',
      blockId,
      promotionName,
      streamPosition,
      path,
    });
  };

  return (
    <SlideWrapper>
      <StyledContentBlockMedia
        imageOrVideo={transformImageVideo(slide.imageVideo)!}
        videoRef={videoElementRef}
        onEndVideoCallback={trackFeatureOnVideoEnds}
      />

      {slide.headline && (
        <StyledSliderHeadline
          testId={`${testId}_Headline`}
          alignment={alignmentMap[slide.headline.alignment]}
          color={slide.headline.color}
          size={fontMap[slide.headline.size]}
          text={slide.headline.text}
          weight={'bold'}
        />
      )}

      {slide.text && (
        <StyledSliderText
          testId={`${testId}_Text`}
          alignment={alignmentMap[slide.text.alignment]}
          color={slide.text.color}
          size={fontMap[slide.text.size]}
          text={slide.text.text}
        />
      )}

      {slide.cta && (
        <CTA
          content={slide.cta}
          onClickCTA={(promotionName, path) =>
            handleCtaClick(promotionName, path, slideIndex)
          }
          blockId={blockId}
        />
      )}
    </SlideWrapper>
  );
};

const StyledRowSection = linariaStyled(RowSection)<{$backgroundColor?: string}>`
  background-color:  ${({$backgroundColor}) => $backgroundColor ?? ''};
  padding-top: 2rem;
  padding-bottom: 3rem;
`;

const StyledHorizontalSlider = linariaStyled(HorizontalSlider)`
    @media ${mediaTabletAndUp} {
      ${SliderWrapper} {
        padding: 0 12rem;
        scroll-padding: 0 12rem;
      }
  }

  @media ${mediaLargeScreens} {
      ${SliderWrapper} {
        padding: 0 17rem;
        scroll-padding: 0 17rem;
      }

  }
`;

const HorizontalSliderWrapper = sc.div`
  &:first-child {
    margin-top: 3rem;
  }

  ${mobileOnly(
    css`
      padding: 0 1rem;
    `,
  )}
`;

const StyledSliderTitle = sc(ContentBlockText)`
  margin-bottom: 3rem;
  &:first-child {
    margin-top: 3rem;
  }
`;

const SlideWrapper = sc.div`
  align-items: center;
  display: flex;
  flex-direction: column;

  ${tabletAndUp(
    css`
      &:nth-child(2n) {
        margin: 0 1.5rem;
      }
    `,
  )}

  ${mobileOnly(
    css`
      margin-right: 6px;
    `,
  )}
`;

const StyledContentBlockMedia = sc(ContentBlockMedia)`
  border-radius: 4px;
  margin-bottom: 2.5rem;

  overflow: hidden;
`;

const StyledSliderHeadline = sc(ContentBlockText)`
  margin-bottom: 0.5rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledSliderText = sc(ContentBlockText)`
  width: 60%;
`;
